import type {
  ColorValue,
  FontFamilyID,
  LengthValue,
  NormalValue,
  TextShadowValue,
  TextColumnsValue,
  StylePanelProperty,
  NumberValue,
  PercentValue,
  FontVariationSettings,
  FontVariationSetting,
  VariableAutoableLengthValue,
  CustomRawPrimitive,
} from '@packages/shared/LegacyStyleSystem/utils/StylePanelUtils';
import {
  StyleStateType,
  FontStyle,
  FontWeight,
  TextAlign,
  TextDecoration,
  TextTransform,
  TextDirection,
  Typography,
  StyleProperty,
  StyleStore$ValueType,
  WhiteSpaceStyle,
  WordBreakStyle,
  OverflowWrapStyle,
  TextWrapMode,
  WhiteSpaceCollapse,
  TextWrapStyle,
  HyphensStyle,
  FontVariation,
  TextOverflow,
  Overflow,
  TextDecorationStyle,
  TextDecorationSkipInk,
  TextUnderlinePosition,
  type PropertyOrigin,
  getPropertyOrigin,
  getCompoundPropertyOrigin,
  hasLocalOrigin,
  hasUpstreamOrigin,
  DefaultOrigin,
} from '@packages/systems/style-legacy/values';

import {
  getAutoNumberValue,
  getColorValue,
  getLengthValue,
  getLengthOrPercentValue,
  getVariableLengthValue,
  getVariableLengthOrPercentValue,
  getPropertyValueWithBrowserDefault,
  getVariablePlaceholderValue,
  getKeywordValue,
  getVariableKeywordValue,
  getNumberValue,
  getTextShadowValue,
  userUnit,
  NORMAL_UNIT_VALUE,
  computedValue,
  getVariableAutoLengthValueOrPercentValue,
} from '@packages/shared/stores/StyleSystem/renderers';
import {emptyArray} from '@packages/utilities/fp/utils';
import {weakMemo} from '@packages/utilities/memo';
import {isRegisteredAxis} from '@packages/systems/core/constants/Fonts';
import type {VariableReference} from '@packages/systems/core/utils/StyleVariables';
type StylePanel$TextValues = {
  fontFamily: StylePanelProperty<
    FontFamilyID | VariableReference | CustomRawPrimitive
  >;
  fontVariationSettings: StylePanelProperty<FontVariationSettings>;
  fontWeight: StylePanelProperty<
    FontWeight | VariableReference | NumberValue | CustomRawPrimitive
  >;
  fontSize: StylePanelProperty<
    LengthValue | PercentValue | VariableReference | CustomRawPrimitive
  >;
  letterSpacing: StylePanelProperty<
    LengthValue | NormalValue | VariableReference
  >;
  lineHeight: StylePanelProperty<
    LengthValue | NumberValue | PercentValue | VariableReference
  >;
  textIndent: StylePanelProperty<
    LengthValue | PercentValue | VariableReference | CustomRawPrimitive
  >;
  color: StylePanelProperty<ColorValue>;
  textAlign: StylePanelProperty<TextAlign>;
  fontStyle: StylePanelProperty<FontStyle>;
  direction: StylePanelProperty<TextDirection>;
  textTransform: StylePanelProperty<TextTransform>;
  textDecoration: StylePanelProperty<TextDecoration>;
  textColumns: StylePanelProperty<TextColumnsValue>;
  textColumnsOrigins: StylePanelProperty<{
    gap: PropertyOrigin;
    ruleColor: PropertyOrigin;
    ruleStyle: PropertyOrigin;
    ruleWidth: PropertyOrigin;
    span: PropertyOrigin;
  }>;
  textOverflow: StylePanelProperty<TextOverflow>;
  textShadows: StylePanelProperty<Array<TextShadowValue>>;
  textStrokeWidth: StylePanelProperty<
    LengthValue | PercentValue | VariableReference | CustomRawPrimitive
  >;
  textStrokeColor: StylePanelProperty<ColorValue>;
  whiteSpace: StylePanelProperty<WhiteSpaceStyle>;
  textWrapMode: StylePanelProperty<TextWrapMode>;
  whiteSpaceCollapse: StylePanelProperty<WhiteSpaceCollapse>;
  textWrapStyle: StylePanelProperty<TextWrapStyle>;
  hyphens: StylePanelProperty<HyphensStyle>;
  wordBreak: StylePanelProperty<WordBreakStyle>;
  overflowWrap: StylePanelProperty<OverflowWrapStyle>;
  overflow: StylePanelProperty<Overflow>;
  textUnderlineOffset: StylePanelProperty<
    | VariableAutoableLengthValue
    | PercentValue
    | VariableReference
    | CustomRawPrimitive
  >;
  textDecorationThickness: StylePanelProperty<
    | VariableAutoableLengthValue
    | PercentValue
    | VariableReference
    | CustomRawPrimitive
  >;
  textDecorationStyle: StylePanelProperty<TextDecorationStyle>;
  textDecorationSkipInk: StylePanelProperty<TextDecorationSkipInk>;
  textUnderlinePosition: StylePanelProperty<TextUnderlinePosition>;
  textDecorationColor: StylePanelProperty<ColorValue>;
};

/**
 * Maps legacy `white-space` shorthand keywords to `text-wrap-mode` +
 * `white-space-collapse` longhands (CSS Text 4).
 */
const deriveTextWrapModeAndCollapseFromWhiteSpace = (
  whiteSpace: WhiteSpaceStyle
): {
  textWrapMode: TextWrapMode;
  whiteSpaceCollapse: WhiteSpaceCollapse;
} => {
  switch (whiteSpace) {
    case 'nowrap':
      return {textWrapMode: 'nowrap', whiteSpaceCollapse: 'collapse'};
    case 'pre':
      return {textWrapMode: 'nowrap', whiteSpaceCollapse: 'preserve'};
    case 'pre-wrap':
      return {textWrapMode: 'wrap', whiteSpaceCollapse: 'preserve'};
    case 'pre-line':
      return {textWrapMode: 'wrap', whiteSpaceCollapse: 'preserve-breaks'};
    case 'break-spaces':
      return {textWrapMode: 'wrap', whiteSpaceCollapse: 'break-spaces'};
    case 'normal':
    case 'initial':
    case 'inherit':
    default:
      return {textWrapMode: 'wrap', whiteSpaceCollapse: 'collapse'};
  }
};

export const getTypographyValues = (
  StyleStore: StyleStateType
): {
  properties: StylePanel$TextValues;
  /**
   * True when the displayed `textWrapMode` / `whiteSpaceCollapse` pair was
   * inferred from the legacy `white-space` shorthand rather than from the
   * longhand stores. Drives reset and rule-click routing in the section so the
   * user's action targets the actual source declaration.
   */
  textWrapPairDerivedFromWhiteSpaceShorthand: boolean;
} => {
  const textDirection = {
    value: getKeywordValue(StyleStore.direction),
    origin: getPropertyOrigin(StyleStore.direction),
  } as const;

  const textAlign = {
    value: getKeywordValue(StyleStore.textAlign),
    origin: getPropertyOrigin(StyleStore.textAlign),
  } as const;

  // if the value is coming from the browser default and it's start/end
  // (which we don't support), then we compute whether it maps to "left"
  // or "right", which depends on the text-direction value
  if (textAlign.value === 'start') {
    // @ts-expect-error - TS2540 - Cannot assign to 'value' because it is a read-only property.
    textAlign.value = textDirection.value === 'ltr' ? 'left' : 'right';
  } else if (textAlign.value === 'end') {
    // @ts-expect-error - TS2540 - Cannot assign to 'value' because it is a read-only property.
    textAlign.value = textDirection.value === 'ltr' ? 'right' : 'left';
  }

  const textColumnsOrigins = {
    gap: getPropertyOrigin(StyleStore.columnGap),
    ruleColor: getPropertyOrigin(StyleStore.columnRuleColor),
    ruleStyle: getPropertyOrigin(StyleStore.columnRuleStyle),
    ruleWidth: getPropertyOrigin(StyleStore.columnRuleWidth),
    span: getPropertyOrigin(StyleStore.columnSpan),
  };

  const textWrapModeStore = StyleStore.textWrapMode;
  const whiteSpaceCollapseStore = StyleStore.whiteSpaceCollapse;

  // True if either longhand has been explicitly set somewhere in the cascade
  // (local or upstream). Checked across both longhands because the user may
  // have set just one of the pair.
  const wrapLonghandExplicit =
    Boolean(textWrapModeStore && hasLocalOrigin(textWrapModeStore)) ||
    Boolean(
      whiteSpaceCollapseStore && hasLocalOrigin(whiteSpaceCollapseStore)
    ) ||
    Boolean(textWrapModeStore && hasUpstreamOrigin(textWrapModeStore)) ||
    Boolean(
      whiteSpaceCollapseStore && hasUpstreamOrigin(whiteSpaceCollapseStore)
    );

  const whiteSpaceOrigin = getPropertyOrigin(StyleStore.whiteSpace);
  const whiteSpaceKeyword = getKeywordValue(
    StyleStore.whiteSpace
  ) as WhiteSpaceStyle;
  const derivedFromWhiteSpace =
    deriveTextWrapModeAndCollapseFromWhiteSpace(whiteSpaceKeyword);

  let textWrapModePanel: StylePanelProperty<TextWrapMode>;
  let whiteSpaceCollapsePanel: StylePanelProperty<WhiteSpaceCollapse>;
  /**
   * Surfaced to the panel so reset / origin actions on the wrap control can
   * target the source the displayed value actually came from. Stays `false`
   * unless we fall through to the legacy `white-space` shorthand.
   */
  let textWrapPairDerivedFromWhiteSpaceShorthand = false;

  // Resolve the displayed `text-wrap-mode` / `white-space-collapse` pair from
  // three possible sources, in priority order:
  //
  //   1. The new longhand stores, if either is explicitly set. Per CSS cascade
  //      semantics a longhand declaration shadows any shorthand, so once the
  //      user touches them they win. When only one of the pair is set, the
  //      other still falls back to the white-space-derived value so the
  //      displayed pair stays internally coherent.
  //   2. The legacy `white-space` shorthand, decomposed into the longhand pair.
  //      The displayed origin is `white-space`'s origin (so the "inherited
  //      from" indicator points at the right rule), and we flag the pair as
  //      shorthand-derived so reset routes through `white-space`.
  //   3. The CSS spec defaults when nothing in the cascade applies.
  if (wrapLonghandExplicit) {
    textWrapModePanel = {
      value: (textWrapModeStore
        ? getKeywordValue(textWrapModeStore)
        : derivedFromWhiteSpace.textWrapMode) as TextWrapMode,
      origin: textWrapModeStore
        ? getPropertyOrigin(textWrapModeStore)
        : whiteSpaceOrigin,
    };
    whiteSpaceCollapsePanel = {
      value: (whiteSpaceCollapseStore
        ? getKeywordValue(whiteSpaceCollapseStore)
        : derivedFromWhiteSpace.whiteSpaceCollapse) as WhiteSpaceCollapse,
      origin: whiteSpaceCollapseStore
        ? getPropertyOrigin(whiteSpaceCollapseStore)
        : whiteSpaceOrigin,
    };
  } else if (whiteSpaceOrigin.type !== 'default') {
    textWrapModePanel = {
      value: derivedFromWhiteSpace.textWrapMode,
      origin: whiteSpaceOrigin,
    };
    whiteSpaceCollapsePanel = {
      value: derivedFromWhiteSpace.whiteSpaceCollapse,
      origin: whiteSpaceOrigin,
    };
    textWrapPairDerivedFromWhiteSpaceShorthand = true;
  } else {
    textWrapModePanel = {
      value: 'wrap',
      origin: DefaultOrigin(),
    };
    whiteSpaceCollapsePanel = {
      value: 'collapse',
      origin: DefaultOrigin(),
    };
  }

  // `text-wrap-style` and `hyphens` have no legacy shorthand equivalent to
  // decompose from, so they're a straight read-with-default — no derivation
  // step needed.
  const textWrapStylePanel = StyleStore.textWrapStyle
    ? ({
        value: getKeywordValue(StyleStore.textWrapStyle) as TextWrapStyle,
        origin: getPropertyOrigin(StyleStore.textWrapStyle),
      } as const)
    : ({
        value: 'auto' as TextWrapStyle,
        origin: DefaultOrigin(),
      } as const);

  const hyphensStore = StyleStore.hyphens;
  const hyphensPanel = hyphensStore
    ? ({
        value: getKeywordValue(hyphensStore) as HyphensStyle,
        origin: getPropertyOrigin(hyphensStore),
      } as const)
    : ({
        value: 'manual' as HyphensStyle,
        origin: DefaultOrigin(),
      } as const);

  const properties: StylePanel$TextValues = {
    color: {
      value: getColorValue(StyleStore.fontColor),
      origin: getPropertyOrigin(StyleStore.fontColor),
    },
    fontWeight: {
      value: getVariableKeywordValue(StyleStore.fontWeight),
      origin: getPropertyOrigin(StyleStore.fontWeight),
    },
    fontSize: {
      value: getVariableLengthOrPercentValue(StyleStore.fontSize),
      origin: getPropertyOrigin(StyleStore.fontSize),
    },
    fontFamily: {
      value: getVariableKeywordValue(StyleStore.fontFamily),
      origin: getPropertyOrigin(StyleStore.fontFamily),
    },
    fontVariationSettings: {
      value: getFontVariationSettingValues(StyleStore.fontVariationSettings),
      origin: getPropertyOrigin(StyleStore.fontVariationSettings),
    },
    lineHeight: {
      value: getVariableTextLineHeight(StyleStore.lineHeight),
      origin: getPropertyOrigin(StyleStore.lineHeight),
    },
    textIndent: {
      value: getVariableLengthOrPercentValue(StyleStore.textIndent),
      origin: getPropertyOrigin(StyleStore.textIndent),
    },
    letterSpacing: {
      value: getVariableLetterSpacingValue(StyleStore.letterSpacing),
      origin: getPropertyOrigin(StyleStore.letterSpacing),
    },
    textAlign,
    fontStyle: {
      value: getKeywordValue(StyleStore.fontStyle),
      origin: getPropertyOrigin(StyleStore.fontStyle),
    },
    direction: textDirection,
    textDecoration: {
      value: getKeywordValue(StyleStore.textDecoration),
      origin: getPropertyOrigin(StyleStore.textDecoration),
    },
    textOverflow: {
      value: getKeywordValue(StyleStore.textOverflow),
      origin: getPropertyOrigin(StyleStore.textOverflow),
    },
    textTransform: {
      value: getKeywordValue(StyleStore.textTransform),
      origin: getPropertyOrigin(StyleStore.textTransform),
    },
    textShadows: {
      value: getTextShadowValue(StyleStore.textShadows),
      origin: getPropertyOrigin(StyleStore.textShadows),
    },
    textStrokeWidth: {
      value: getVariableLengthValue(StyleStore.textStrokeWidth),
      origin: getPropertyOrigin(StyleStore.textStrokeWidth),
    },
    textStrokeColor: {
      value: getColorValue(StyleStore.textStrokeColor),
      origin: getPropertyOrigin(StyleStore.textStrokeColor),
    },
    textUnderlineOffset: {
      value: getVariableAutoLengthValueOrPercentValue(
        StyleStore.textUnderlineOffset
      ),
      origin: getPropertyOrigin(StyleStore.textUnderlineOffset),
    },
    textDecorationThickness: {
      value: getVariableAutoLengthValueOrPercentValue(
        StyleStore.textDecorationThickness
      ),
      origin: getPropertyOrigin(StyleStore.textDecorationThickness),
    },
    textDecorationStyle: {
      value: getKeywordValue(StyleStore.textDecorationStyle),
      origin: getPropertyOrigin(StyleStore.textDecorationStyle),
    },
    textDecorationSkipInk: {
      value: getKeywordValue(StyleStore.textDecorationSkipInk),
      origin: getPropertyOrigin(StyleStore.textDecorationSkipInk),
    },
    textUnderlinePosition: {
      value: getKeywordValue(StyleStore.textUnderlinePosition),
      origin: getPropertyOrigin(StyleStore.textUnderlinePosition),
    },
    textDecorationColor: {
      value: getColorValue(StyleStore.textDecorationColor),
      origin: getPropertyOrigin(StyleStore.textDecorationColor),
    },
    textColumns: {
      value: {
        gap: getVariableLengthOrPercentValue(StyleStore.columnGap),
        ruleColor: getColorValue(StyleStore.columnRuleColor),
        ruleStyle: getKeywordValue(StyleStore.columnRuleStyle),
        ruleWidth: getVariableLengthValue(StyleStore.columnRuleWidth),
        span: getKeywordValue(StyleStore.columnSpan),
        columnCount: getTextColumnCount(StyleStore.columnCount),
      },
      origin: getCompoundPropertyOrigin(
        StyleStore.columnCount,
        StyleStore.columnGap,
        StyleStore.columnRuleColor,
        StyleStore.columnRuleStyle,
        StyleStore.columnRuleWidth,
        StyleStore.columnSpan
      ),
    },
    textColumnsOrigins: {
      value: textColumnsOrigins,
      origin: getCompoundPropertyOrigin(
        StyleStore.columnGap,
        StyleStore.columnRuleColor,
        StyleStore.columnRuleStyle,
        StyleStore.columnRuleWidth,
        StyleStore.columnSpan
      ),
    },
    whiteSpace: {
      value: getKeywordValue(StyleStore.whiteSpace),
      origin: getPropertyOrigin(StyleStore.whiteSpace),
    },
    textWrapMode: textWrapModePanel,
    whiteSpaceCollapse: whiteSpaceCollapsePanel,
    textWrapStyle: textWrapStylePanel,
    hyphens: hyphensPanel,
    wordBreak: {
      value: getKeywordValue(StyleStore.wordBreak),
      origin: getPropertyOrigin(StyleStore.wordBreak),
    },
    overflowWrap: {
      value: getKeywordValue(StyleStore.overflowWrap),
      origin: getPropertyOrigin(StyleStore.overflowWrap),
    },
    // While this is not a text value, we want o include so we can render
    // a notification if the overflow is set to hidden
    overflow: {
      value: getKeywordValue(StyleStore.overflow),
      origin: getPropertyOrigin(StyleStore.overflow),
    },
  };

  return {
    properties,
    textWrapPairDerivedFromWhiteSpaceShorthand,
  };
};

// textColumns.columnCount wants 'auto' | number
// instead of AutoValue | NumberValue
const getTextColumnCount = (
  columnCount: Typography['columnCount']
): 'auto' | number | string | VariableReference => {
  const value = getPropertyValueWithBrowserDefault(columnCount);
  if (typeof value === 'string') {
    const variableRef = getVariablePlaceholderValue(value);
    if (variableRef) {
      return variableRef;
    }
  }

  const unitValue = getAutoNumberValue(columnCount);
  return unitValue.value;
};

const getVariableTextLineHeight = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): NumberValue | LengthValue | PercentValue | VariableReference => {
  const value = getPropertyValueWithBrowserDefault(property);
  const variableRef = getVariablePlaceholderValue(value);
  if (variableRef) {
    return variableRef;
  }

  const unit = userUnit(property);
  if (unit === null) {
    return getNumberValue(property);
  } else {
    return getLengthOrPercentValue(property);
  }
};

const getVariableLetterSpacingValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): NormalValue | LengthValue | VariableReference => {
  const value = getPropertyValueWithBrowserDefault(property);
  const variableRef = getVariablePlaceholderValue(value);
  if (variableRef) {
    return variableRef;
  }

  const unit = userUnit(property);
  if (unit === null) {
    return NORMAL_UNIT_VALUE;
  } else {
    return getLengthValue(property);
  }
};

export const getFontVariationSettingType = weakMemo(
  function _getFontVariationSettingType(setting: FontVariation): string | null {
    let upstreamType = null;

    for (const key in setting) {
      // Skip _guid and _type because it is not a font variable setting;
      if (key === '_guid' || key === '_type' || key === 'hidden') continue;

      const property = setting[key];
      // @ts-expect-error - TS2345 - Argument of type 'StyleProperty<LengthValue> | undefined' is not assignable to parameter of type 'StyleProperty<any>'.
      if (hasLocalOrigin(property)) {
        // Local value has a higher priority.
        return key;
      }
      // @ts-expect-error - TS2345 - Argument of type 'StyleProperty<LengthValue> | undefined' is not assignable to parameter of type 'StyleProperty<any>'.
      if (hasUpstreamOrigin(property)) {
        // We still want to use upstream value in case there will be no local value found.
        upstreamType = key;
      }
    }

    return upstreamType;
  }
);

export const getFontVariationSettingValues = weakMemo(
  <T extends StyleStore$ValueType>(
    property: StyleProperty<T>
  ): FontVariationSettings => {
    const fontVariationSettings = computedValue(property);
    if (
      Array.isArray(fontVariationSettings) &&
      fontVariationSettings.length > 0
    ) {
      const result: Array<FontVariationSetting> = [];

      // @ts-expect-error - TS2488 - Type 'T["value"] & any[]' must have a '[Symbol.iterator]()' method that returns an iterator.
      for (const setting of fontVariationSettings) {
        // unlike backgrounds, font variation settings's don't have a key to check against
        // instead we have to check for which property actually has a value
        const tag = getFontVariationSettingType(setting);
        if (tag == null) continue;

        result.push({
          id: setting._guid,
          type: isRegisteredAxis(tag) ? 'registered-axis' : 'custom-axis',
          tag,
          hidden: computedValue(setting.hidden),
          value: getLengthValue(setting[tag]).value,
        });
      }

      return result;
    } else {
      return emptyArray;
    }
  }
);

export const forTestSuite = {
  deriveTextWrapModeAndCollapseFromWhiteSpace,
};
